import { put } from 'redux-saga/effects';

import { store } from '../store';
import { send } from '../../helpers/requests';
import * as actions from './actions';
import * as timesheetsActions from '../Timesheets/actions';
import * as timesheetEntriesActions from '../TimesheetEntries/actions';
import * as timesheetRowsActions from '../TimesheetRows/actions';
import toastr from '../../helpers/toastr';
import AnalyticsClient from '../../analytics/analyticsClient';
import trackEvents from '../../analytics/trackEvents';
import { selectToken } from '../selectors';

export function* createTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: '/timesheets',
      method: 'POST',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.createTimesheetRequestSuccess(res));
    yield put(timesheetsActions.getTimesheetsRequest());
    yield put(timesheetRowsActions.createTimesheetRowRequest({ timesheetId: res.id }));
    payload.data.history.push(`/timesheet/${res.id}`);
    AnalyticsClient.track(trackEvents.TIMESHEET_CREATE);
  } catch (error) {
    yield put(actions.createTimesheetRequestFailure(error));
  }
}

export function* deleteTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}`,
      method: 'DELETE',
      token: selectToken(store.getState()),
    });
    yield put(actions.deleteTimesheetRequestSuccess(res));
    yield put(timesheetsActions.deleteSingleTimesheetRequestSuccess({ id: payload.data.id }));
    AnalyticsClient.track(trackEvents.TIMESHEET_DELETE);
  } catch (error) {
    yield put(actions.deleteTimesheetRequestFailure(error));
  }
}

export function* duplicateTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}/duplicate`,
      method: 'POST',
      token: selectToken(store.getState()),
      data: payload.data.data,
    });
    yield put(actions.duplicateTimesheetRequestSuccess(res));
    yield put(timesheetsActions.getTimesheetsRequest());
    payload.data.history.push(`/timesheet/${res.id}`);
    AnalyticsClient.track(trackEvents.TIMESHEET_DUPLICATE);
  } catch (error) {
    yield put(actions.duplicateTimesheetRequestFailure(error));
  }
}

export function* getTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}`,
      method: 'GET',
      token: selectToken(store.getState()),
    });
    yield put(actions.getTimesheetRequestSuccess(res));
  } catch (error) {
    yield put(actions.getTimesheetRequestFailure(error));
  }
}

export function* recallTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}/submit/undo`,
      method: 'POST',
      token: selectToken(store.getState()),
    });
    yield put(actions.recallTimesheetRequestSuccess(res));
    toastr.success('Your timesheet has been successfully recalled');
    AnalyticsClient.track(trackEvents.TIMESHEET_RECALL);
  } catch (error) {
    yield put(actions.recallTimesheetRequestFailure(error));
  }
}

export function* saveTimesheetRequestAsyncSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}/save-async`,
      method: 'POST',
      token: selectToken(store.getState()),
    });
    yield put(actions.saveTimesheetRequestSuccess(res));
    yield put(actions.getTimesheetRequest({ id: payload.data.id }));
  } catch (error) {
    yield put(actions.saveTimesheetRequestFailure(error));
  }
}

export function* saveTimesheetRequestAsyncSagaFailure(payload) {
  const timesheetId = payload.error?.id;
  yield put(timesheetRowsActions.getTimesheetRowsRequest({ timesheetId }));
  yield put(timesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId, showToast: true }));
}

export function* saveTimesheetRequestAsyncSagaSuccess(payload) {
  yield put(timesheetRowsActions.getTimesheetRowsRequest({ timesheetId: payload.data.id }));
  yield put(timesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId: payload.data.id, showToast: true }));
  AnalyticsClient.track(trackEvents.TIMESHEET_SAVE);
}

export function* submitTimesheetRequestSaga(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}/submit`,
      method: 'POST',
      token: selectToken(store.getState()),
    });
    yield put(actions.submitTimesheetRequestSuccess(res));
    toastr.success('Your timesheet has been successfully submitted');
    AnalyticsClient.track(trackEvents.TIMESHEET_SUBMIT);
  } catch (error) {
    yield put(actions.submitTimesheetRequestFailure(error));
  } finally {
    yield put(timesheetRowsActions.getTimesheetRowsRequest({ timesheetId: payload.data.id }));
    yield put(timesheetsActions.getTimesheetsRequest());
    yield put(timesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId: payload.data.id, showToast: true }));
    if (payload.data.refetchTimesheet) yield put(actions.getTimesheetRequest({ id: payload.data.id }));
  }
}

export function* submitTimesheetRequestSagaAsync(payload) {
  try {
    const res = yield send({
      url: `/timesheets/${payload.data.id}/submit-async`,
      method: 'POST',
      token: selectToken(store.getState()),
    });
    yield put(actions.submitTimesheetRequestSuccess(res));
  } catch (error) {
    yield put(actions.submitTimesheetRequestFailure(error));
  }
}

export function* submitTimesheetRequestSagaAsyncFailure(payload) {
  const timesheetId = payload.error?.id;
  yield put(timesheetRowsActions.getTimesheetRowsRequest({ timesheetId }));
  yield put(timesheetsActions.getTimesheetsRequest());
  yield put(timesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId, showToast: true }));
  if (payload.error?.refetchTimesheet) yield put(actions.getTimesheetRequest({ id: timesheetId }));
}

export function* submitTimesheetRequestSagaAsyncSuccess(payload) {
  yield put(timesheetRowsActions.getTimesheetRowsRequest({ timesheetId: payload.data.id }));
  yield put(timesheetsActions.getTimesheetsRequest());
  yield put(timesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId: payload.data.id, showToast: true }));
  if (payload.data.refetchTimesheet) yield put(actions.getTimesheetRequest({ id: payload.data.id }));
  AnalyticsClient.track(trackEvents.TIMESHEET_SUBMIT);
}

export function* updateTimesheetRequestSaga(payload) {
  try {
    if (!payload.data.id) return;
    const res = yield send({
      url: `/timesheets/${payload.data.id}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateTimesheetRequestSuccess(res));
    const data = {
      id: payload.data.id,
    };
    yield put(actions.getTimesheetRequest(data));
    AnalyticsClient.track(trackEvents.TIMESHEET_UPDATE);
  } catch (error) {
    yield put(actions.updateTimesheetRequestFailure(error));
  }
}
